import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetch } from './api';

// Error codes the backend can return from POST /api/google/callback. Each has
// its own translated message; anything else falls back to failedAuth.
const KNOWN_ERRORS = ['state_invalid', 'state_wrong_user', 'state_used', 'exchange_failed', 'scope_missing', 'no_refresh_token'];

/**
 * Google sends the browser here after the consent screen. The code is posted
 * to the backend with the session cookie, so only the ReviewCounter account
 * that started the connection can finish it.
 */
const GoogleAuthCallback = () => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // The backend accepts each sign-in exactly once, and React's StrictMode runs
  // effects twice in development; without this the second run would always
  // fail with "already used".
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // The person declined on Google's consent screen.
    if (searchParams.get('error')) {
      setError(t('googleCallback.declined'));
      return;
    }
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    // Out of the address bar and history right away: the code is single-use,
    // but it has no business sitting in a URL someone might copy or share.
    window.history.replaceState(null, '', window.location.pathname);
    if (!code || !state) {
      setError(t('googleCallback.missingParams'));
      return;
    }

    (async () => {
      try {
        const res = await apiFetch('/api/google/callback', {
          method: 'POST',
          body: JSON.stringify({ code, state }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          setError(KNOWN_ERRORS.includes(data.code) ? t(`googleCallback.errors.${data.code}`) : t('googleCallback.failedAuth'));
          return;
        }
        navigate('/restaurant?google=connected', { replace: true });
      } catch {
        setError(t('googleCallback.failedAuth'));
      }
    })();
  }, [navigate, searchParams, t]);

  return (
    <div className="max-w-md mx-auto mt-16 bg-white rounded-lg shadow p-6 text-center">
      {error ? (
        <>
          <h1 className="text-lg font-semibold text-red-700 mb-2">{t('googleCallback.errorTitle')}</h1>
          <p className="text-gray-700 mb-4">{error}</p>
          <Link to="/restaurant" className="text-teal-700 hover:underline">{t('googleCallback.backToRestaurant')}</Link>
        </>
      ) : (
        <p className="text-gray-700">{t('googleCallback.loading')}</p>
      )}
    </div>
  );
};

export default GoogleAuthCallback;
